import React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { EditableLineItem } from '@payer-admin-ui/api/src/orders/models/LineItem';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';
import { TextInput } from '../Input/TextInput';
import { AmountInput } from '../Input/AmountInput';

export type LineItemInputsProps = {
  item: EditableLineItem;
  patchItem: (itemPatch: EditableLineItem) => void;
  currencyCode: CurrencyCode;
};
const LineItemInputs = ({
  item: {
    description,
    articleNumber,
    quantity,
    subtotalPrice,
    subtotalVatAmount,
    vatPercentage,
    unitPrice,
    unitVatAmount,
    unit,
    vatAccountCode,
    salesAccountCode,
    costCenter,
    additionalDetail,
    ean,
    uri,
  },
  patchItem,
  currencyCode,
}: LineItemInputsProps) => {
  const { t } = useTranslation();

  return (
    <Row>
      <Col xs="12" sm="6" lg="4">
        <TextInput
          title={t('invoices.fields.lineItem.fields.description')}
          value={description}
          setValue={description => patchItem({ description: description || undefined })}
          validators={[Validators.notEmpty, Validators.length(0, 128)]}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <TextInput
          title={t('invoices.fields.lineItem.fields.articleNumber')}
          value={articleNumber}
          setValue={articleNumber => patchItem({ articleNumber: articleNumber || undefined })}
          validators={[Validators.notEmpty, Validators.length(0, 64)]}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.quantity')}
          value={quantity}
          setValue={quantity => patchItem({ quantity: quantity || undefined })}
          validators={[Validators.notEmpty, Validators.integer, Validators.notZero, Validators.positive]}
          unit={unit}
          placeholder="1"
          step={1}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.vatPercentage')}
          value={vatPercentage}
          setValue={vatPercentage => patchItem({ vatPercentage: vatPercentage || undefined })}
          validators={[Validators.notEmpty, Validators.percentage, Validators.max2FractionalDigits]}
          unit="%"
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.subtotalPrice')}
          value={subtotalPrice}
          setValue={subtotalPrice => patchItem({ subtotalPrice: subtotalPrice || undefined })}
          validators={[Validators.notEmpty, Validators.max2FractionalDigits]}
          unit={currencyCode}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.subtotalVatAmount')}
          value={subtotalVatAmount}
          setValue={subtotalVatAmount => patchItem({ subtotalVatAmount: subtotalVatAmount || undefined })}
          validators={[Validators.notEmpty, Validators.max2FractionalDigits]}
          unit={currencyCode}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.unitPrice')}
          value={unitPrice}
          setValue={unitPrice => patchItem({ unitPrice: unitPrice || undefined })}
          validators={[Validators.validIfEmpty, Validators.max2FractionalDigits]}
          unit={currencyCode}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.unitVatAmount')}
          value={unitVatAmount}
          setValue={unitVatAmount => patchItem({ unitVatAmount: unitVatAmount || undefined })}
          validators={[Validators.validIfEmpty, Validators.max2FractionalDigits]}
          unit={currencyCode}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <TextInput
          title={t('invoices.fields.lineItem.fields.unit')}
          value={unit}
          setValue={unit => patchItem({ unit: unit || undefined })}
          validators={[Validators.validIfEmpty, Validators.length(0, 16)]}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.vatAccountCode')}
          value={vatAccountCode}
          setValue={vatAccountCode => patchItem({ vatAccountCode: vatAccountCode || undefined })}
          validators={[Validators.validIfEmpty, Validators.number]}
          placeholder=""
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <AmountInput
          title={t('invoices.fields.lineItem.fields.salesAccountCode')}
          value={salesAccountCode}
          setValue={salesAccountCode => patchItem({ salesAccountCode: salesAccountCode || undefined })}
          validators={[Validators.validIfEmpty, Validators.integer]}
          placeholder=""
          step={1}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <TextInput
          title={t('invoices.fields.lineItem.fields.costCenter')}
          value={costCenter}
          setValue={costCenter => patchItem({ costCenter: costCenter || undefined })}
          validators={[Validators.validIfEmpty, Validators.length(0, 48)]}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <TextInput
          title={t('invoices.fields.lineItem.fields.additionalDetail')}
          value={additionalDetail}
          setValue={additionalDetail => patchItem({ additionalDetail: additionalDetail || undefined })}
          validators={[Validators.validIfEmpty, Validators.length(0, 48)]}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <TextInput
          title={t('invoices.fields.lineItem.fields.ean')}
          value={ean}
          setValue={ean => patchItem({ ean: ean || undefined })}
          validators={[Validators.validIfEmpty, Validators.length(0, 64)]}
        />
      </Col>
      <Col xs="12" sm="6" lg="4">
        <TextInput
          title={t('invoices.fields.lineItem.fields.uri')}
          value={uri}
          setValue={uri => patchItem({ uri: uri || undefined })}
          validators={[Validators.validIfEmpty, Validators.length(0, 248)]}
        />
      </Col>
    </Row>
  );
};

export { LineItemInputs };
